import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { AppConfiguration } from '@schemas/app.interface';
import { SearchService } from '@services/search/search.service';
import { Filters, SearchRequest } from '@schemas/search/search-request';
import { CurrencyPipe } from '@angular/common';
import { SearchOperatorsEnum } from '@shared/enums/search-api.enum';
import { ColorService } from '@services/color.service';

export enum FilterBoxInputTypeEnum {
  CHECKBOX,
  RADIOBUTTON,
}

@Component({
  selector: 'tvb-filter-box',
  templateUrl: './filter-box.component.html',
  styleUrls: ['./filter-box.component.scss'],
})
export class FilterBoxComponent implements OnInit, OnChanges {
  @Input() title: string;
  @Input() filterName: string; // the same displayed in the URL
  @Input() accessProperty: string; // the property to get from the response model
  @Input() displayProperty: string; // the value to be displayed for the final user
  @Input() mainProperty?: string;
  @Input() inputPlaceholder: string;
  @Input() options: any[] = [];
  @Input() appConfig: AppConfiguration;
  @Input() isQuickSearchable = false;
  @Input() inputType: FilterBoxInputTypeEnum = FilterBoxInputTypeEnum.CHECKBOX;
  @Input() isExpanded: boolean = true;
  @Output() toggleSectionOption: EventEmitter<{
    filterName: string;
    filterValue: string;
    filterOperator: string;
    inputType: FilterBoxInputTypeEnum;
    filterId?: string;
  }> = new EventEmitter();
  @ContentChild('conditionsInfo', { static: false }) conditionsInfoTemplateRef: TemplateRef<any>;
  @ContentChild('shoesSizeInfo', { static: false }) shoesSizeInfoTemplateRef: TemplateRef<any>;
  @ContentChild('clothingSizeInfo', { static: false }) clothingSizeInfoTemplateRef: TemplateRef<any>;
  @ContentChild('priceSlider', { static: false }) priceSliderTemplateRef: TemplateRef<any>;
  quickSearchResultOptions: any[] = [];
  visibleOptions: any[] = [];
  FilterBoxInputTypeEnum = FilterBoxInputTypeEnum;
  selectedOptionValue;
  private sharedSearchRequest: SearchRequest;
  public searchRequest: SearchRequest = {
    page: 0,
    pageSize: 100,
    filters: [],
    sorting: null,
    currency: '',
    language: '',
  };

  constructor(
    private searchService: SearchService,
    private currencyPipe: CurrencyPipe,
    private colorService: ColorService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.appConfig?.previousValue != changes.appConfig?.currentValue) {
      this.appConfig = changes.appConfig.currentValue;
      this.searchRequest.currency = this.appConfig?.currencyCode || 'eur';
      this.searchRequest.language = this.appConfig?.languageShortName || 'en';
    }

    if (changes.options?.previousValue != changes.options?.currentValue) {
      this.options = changes.options.currentValue;
      this.visibleOptions = this.options;

      if (this.filterName === 'price' && this.sharedSearchRequest) {
        const selectedOptionFilter: Filters = this.sharedSearchRequest.filters.find(
          filter => filter.field.toString().toLowerCase() === this.filterName.toString().toLowerCase()
        );

        this.selectedOptionValue = selectedOptionFilter?.value;
      }
    }
  }

  ngOnInit(): void {
    this.visibleOptions = this.options;
    this.searchService.searchRequest.subscribe(data => {
      this.sharedSearchRequest = data;
    });

    if (this.filterName === 'color' && !['en', 'uk'].includes(this.appConfig.languageShortName)) {
      this.colorService.getColors().subscribe(colors => {
        if (colors) {
          // O(n2) solution until we enhance it.
          this.visibleOptions = this.options.map(option => {
            const colorEn = colors.filter(c => {
              return c[`color_${this.getLanguage(this.appConfig.languageShortName)}`] === option.colorName;
            });

            return { ...option, colorEn: colorEn[0]?.['color_en'] };
          });
        }
      });
    }
  }

  getLanguage(langShortName: string) {
    switch (langShortName) {
      case 'dk':
        return 'da';
      case 'se':
        return 'sv';
      default:
        return langShortName;
    }
  }

  toggleSection(): void {
    this.isExpanded = !this.isExpanded;
  }

  onToggleSectionOption(filterName: string, filterValue: string, filterId?: string, filterOperator?: string): void {
    this.selectedOptionValue = filterValue;
    this.toggleSectionOption.emit({ filterName, filterValue, filterOperator, inputType: this.inputType, filterId });
  }

  shouldOptionBeSelected(filterName: string, filterValue: string): boolean {
    if (!filterValue) return false;

    const selectedOptionFilter: Filters = this.sharedSearchRequest.filters
      .filter(filter => filter.field.toString().toLowerCase() === filterName.toString().toLowerCase())
      .find(filter => filter.value.toString().toLowerCase() === filterValue.toString().toLowerCase());

    this.selectedOptionValue = selectedOptionFilter?.value;

    return !!selectedOptionFilter;
  }

  onQuickSearch(term: string): void {
    window.setTimeout(() => {
      this.searchRequest.filters = this.searchService.searchRequest?.value?.filters;
      this.searchRequest.filters = this.searchRequest.filters.filter(x => x.field !== this.filterName);

      if (term)
        this.searchRequest.filters.push({ field: this.filterName, value: term, operator: SearchOperatorsEnum.LIKE });

      this.searchService.getSectionFilter(this.searchRequest, this.filterName).subscribe(data => {
        this.visibleOptions = data[this.mainProperty];
      });
    }, 500);
  }
}
